import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  RefreshCcw,
  Plus,
  Trash2,
  Save,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const emptyRule = {
  name: "",
  category: "all",
  city: "",
  video_url: "",
  click_through_url: "",
  advertiser_name: "",
  cta_text: "Learn more",
  skip_after_seconds: 5,
  priority: 0,
  enabled: true,
};

export default function VideoRules() {
  const [cfg, setCfg] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, mode: "create", rule: emptyRule });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: cats }] = await Promise.all([
        api.get("/ad-config"),
        api.get("/categories"),
      ]);
      setCfg(c);
      setCategories(cats);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rules = cfg?.video_ad?.rules || [];

  const saveRule = async () => {
    const r = dlg.rule;
    if (!r.name || !r.video_url) {
      toast.error("Name and Video URL are required");
      return;
    }
    try {
      if (dlg.mode === "create") {
        await api.post("/ad-config/video-rules", r);
        toast.success("Rule added");
      } else {
        await api.put(`/ad-config/video-rules/${r.id}`, r);
        toast.success("Rule updated");
      }
      setDlg({ open: false, mode: "create", rule: emptyRule });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this video ad rule?")) return;
    try {
      await api.delete(`/ad-config/video-rules/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleVideoAdEnabled = async (v) => {
    try {
      const next = { ...cfg.video_ad, enabled: v };
      await api.put("/ad-config", { video_ad: next });
      setCfg({ ...cfg, video_ad: next });
      toast.success(v ? "Video ads enabled" : "Video ads disabled");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (loading || !cfg) {
    return (
      <div className="p-10 text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Sponsored video
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Video Ad Rules
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Target sponsored video ads by category and city. Higher priority wins.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            data-testid="video-refresh-btn"
            className="h-10"
          >
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            onClick={() => setDlg({ open: true, mode: "create", rule: { ...emptyRule } })}
            data-testid="video-add-btn"
            className="h-10"
          >
            <Plus className="w-4 h-4 mr-2" /> New rule
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
            <Video className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-bold">Video ads master switch</div>
            <div className="text-xs text-muted-foreground">
              {rules.length} rule{rules.length === 1 ? "" : "s"} configured
            </div>
          </div>
        </div>
        <Switch
          checked={!!cfg.video_ad?.enabled}
          onCheckedChange={toggleVideoAdEnabled}
          data-testid="video-master-switch"
        />
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {rules.length === 0 ? (
          <div className="p-12 text-center">
            <Video className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <div className="text-sm font-medium">No video ad rules yet</div>
            <div className="text-xs text-muted-foreground mt-1">
              Create your first sponsored video rule.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-secondary/50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Name</th>
                <th className="text-left px-5 py-3 font-semibold">Category</th>
                <th className="text-left px-5 py-3 font-semibold">City</th>
                <th className="text-left px-5 py-3 font-semibold">Priority</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="video-rules-table">
              {rules.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/30">
                  <td className="px-5 py-4">
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-xs text-muted-foreground mono truncate max-w-xs">
                      {r.video_url}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-secondary text-xs font-medium">
                      {r.category}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-muted-foreground">{r.city || "—"}</td>
                  <td className="px-5 py-4 font-semibold">{r.priority}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                        r.enabled ? "text-emerald-600" : "text-muted-foreground"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          r.enabled ? "bg-emerald-500 pulse-dot" : "bg-muted-foreground"
                        }`}
                      />
                      {r.enabled ? "Live" : "Paused"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setDlg({ open: true, mode: "edit", rule: { ...r } })
                      }
                      data-testid={`video-edit-${r.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => del(r.id)}
                      data-testid={`video-delete-${r.id}`}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog */}
      <Dialog
        open={dlg.open}
        onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {dlg.mode === "create" ? "Add video ad rule" : "Edit rule"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <Field
              label="Rule name"
              value={dlg.rule.name}
              onChange={(v) => setDlg((s) => ({ ...s, rule: { ...s.rule, name: v } }))}
              testId="rule-name-input"
            />
            <Field
              label="Advertiser name"
              value={dlg.rule.advertiser_name}
              onChange={(v) =>
                setDlg((s) => ({ ...s, rule: { ...s.rule, advertiser_name: v } }))
              }
              testId="rule-advertiser-input"
            />
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Category
              </Label>
              <select
                value={dlg.rule.category}
                onChange={(e) =>
                  setDlg((s) => ({ ...s, rule: { ...s.rule, category: e.target.value } }))
                }
                data-testid="rule-category-select"
                className="mt-1.5 w-full h-11 rounded-lg border border-input bg-background px-3 text-sm font-medium"
              >
                <option value="all">All categories</option>
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <Field
              label="City (optional)"
              value={dlg.rule.city}
              onChange={(v) => setDlg((s) => ({ ...s, rule: { ...s.rule, city: v } }))}
              testId="rule-city-input"
            />
            <div className="md:col-span-2">
              <Field
                label="Video URL"
                value={dlg.rule.video_url}
                onChange={(v) =>
                  setDlg((s) => ({ ...s, rule: { ...s.rule, video_url: v } }))
                }
                testId="rule-video-url-input"
                mono
              />
            </div>
            <div className="md:col-span-2">
              <Field
                label="Click-through URL"
                value={dlg.rule.click_through_url}
                onChange={(v) =>
                  setDlg((s) => ({
                    ...s,
                    rule: { ...s.rule, click_through_url: v },
                  }))
                }
                testId="rule-ct-url-input"
                mono
              />
            </div>
            <Field
              label="CTA text"
              value={dlg.rule.cta_text}
              onChange={(v) =>
                setDlg((s) => ({ ...s, rule: { ...s.rule, cta_text: v } }))
              }
              testId="rule-cta-input"
            />
            <Field
              label="Skip after (seconds)"
              type="number"
              value={dlg.rule.skip_after_seconds}
              onChange={(v) =>
                setDlg((s) => ({
                  ...s,
                  rule: { ...s.rule, skip_after_seconds: Number(v) },
                }))
              }
              testId="rule-skip-input"
            />
            <Field
              label="Priority"
              type="number"
              value={dlg.rule.priority}
              onChange={(v) =>
                setDlg((s) => ({ ...s, rule: { ...s.rule, priority: Number(v) } }))
              }
              testId="rule-priority-input"
            />
            <div className="flex items-center justify-between md:col-span-2 pt-1">
              <Label className="text-sm font-semibold">Enabled</Label>
              <Switch
                checked={!!dlg.rule.enabled}
                onCheckedChange={(v) =>
                  setDlg((s) => ({ ...s, rule: { ...s.rule, enabled: v } }))
                }
                data-testid="rule-enabled-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDlg((s) => ({ ...s, open: false }))}
              data-testid="rule-cancel-btn"
            >
              Cancel
            </Button>
            <Button onClick={saveRule} data-testid="rule-save-btn">
              <Save className="w-4 h-4 mr-2" />{" "}
              {dlg.mode === "create" ? "Add rule" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testId, mono }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    <Input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`mt-1.5 h-11 ${mono ? "mono text-sm" : ""}`}
    />
  </div>
);
